import type {
  Address,
  Demonstrator,
  PaymentInformation,
  RecurlyPaymentCreateResponse,
  RecurlyPaymentInformation
} from '~/types/account'
import type { CouponCode } from '~/types/cart'
import type { ApiErrorResponse } from '~/utils/http/api-error'

export interface ShippingMethod {
  id: number
  cost: number
  daysToShip: string
  formattedCost: string
  formattedTotal: string
  isExpedited: boolean
  isSelected: boolean
  isUPS3Day: boolean
  name: string
  shippingNote: string
  total: number
}

export interface Invoice extends RecurlyPaymentCreateResponse {
  invoiceId: string
  demoId: number
  savedShippingMethodId: number
  shippingAddress: Address
  paymentAccount: RecurlyPaymentInformation
  authorizationId?: number
  subTotal: number
  commissionableMerchandiseAmount?: string
  formattedCommissionableMerchandiseAmount?: string
  commissionableProductAmount?: string
  formattedCommissionableProductAmount?: string
  nonCommissionableMerchandiseAmount?: string
  formattedNonCommissionableMerchandiseAmount?: string
  nonCommissionableProductAmount?: string
  formattedNonCommissionableProductAmount?: string
  commissionableRetailValue?: number
  formattedCommissionableRetailValue?: string
  nonCommissionableRetailValue?: number
  formattedNonCommissionableRetailValue?: string
  formattedAmountEligibleForInstantIncome?: string
  instantIncomePercent?: number
  formattedInstantIncomePercent?: string
  instantIncomeAmount?: number
  formattedInstantIncomeAmount?: string
  formattedSubTotal: string
  shipping: number
  formattedShipping: string
  formattedRetailDeliveryFee?: string
  tax: number
  formattedTax: string
  countryTaxAmount?: string
  regionTaxAmount?: string
  csvAmount: number
  formattedCsvAmount: string
  total: number
  formattedTotal: string
  formattedCouponAmount: string
  errors: string[]
  apiError?: ApiErrorResponse
  couponDiscount: number
  formattedCouponDiscount: string
  loyaltyPointsEarned?: number
  formattedLoyaltyPointsEarned?: string
  loyaltyPointsRedeemed?: number
  formattedLoyaltyPointsRedeemed?: string
  compPlanRewards?: number
  formattedCompPlanRewards?: string
  promotionDiscount?: number
  formattedPromotionDiscount?: string
  invoiceHash?: string
  recurlyWants3DSecure?: boolean
  recurlyActionTokenId?: string
  paypalFunding?: string | null
  isCatalogMailing?: boolean
  isDemoOrder?: boolean
  isGift?: boolean
  groupShippingCode?: string
}

export interface InvoiceSubscription extends Invoice {
    firstKitShips: string
    employeeDiscount?: string
    isGift: boolean
    isPrePaid: boolean
    id: string
    giftRecipientEmail?: string
    legacyPrePaidCode?: string
    giftRecipientMessage?: string
    subscriptionTitle?: string
    appliedCouponCodes: CouponCode[]
    demonstrator: Demonstrator
    isClaimed: boolean
}

export interface InvoiceConfirmation {
  invoiceId: string
  employeeDiscount?: string
  groupShippingCode: string
  firstKitShips?: string
  merchandiseSubtotal: string
  commissionableMerchandiseAmount?: string
  formattedCommissionableMerchandiseAmount?: string
  commissionableProductAmount?: string
  formattedCommissionableProductAmount?: string
  nonCommissionableMerchandiseAmount?: string
  formattedNonCommissionableMerchandiseAmount?: string
  nonCommissionableProductAmount?: string
  formattedNonCommissionableProductAmount?: string
  commissionableRetailValue?: number
  formattedCommissionableRetailValue?: string
  nonCommissionableRetailValue?: number
  formattedNonCommissionableRetailValue?: string
  formattedAmountEligibleForInstantIncome?: string
  formattedSubTotal?: string
  orderConfirmationNumber: string
  orderDate: string
  orderTotal: string
  orderTotalNumeric: number
  demonstratorName?: string
  uplineName?: string
  csvAmount: number
  formattedCsvAmount: string
  instantIncomePercent: number
  formattedInstantIncomeAmount?: string
  compPlanRewards?: number
  formattedCompPlanRewards?: string
  loyaltyPointsEarned?: number
  formattedLoyaltyPointsEarned?: string
  loyaltyPointsRedeemed?: number
  formattedLoyaltyPointsRedeemed?: string
  promotionDiscount?: number
  formattedPromotionDiscount?: string
  legacyPrePaidCode?: string
  subscriptionTitle?: string
  orderPayment: RecurlyPaymentInformation | null
  items: [
    {
      itemId: string
      description: string
      price: string
      quantity: number
    }
  ]
  couponDiscount: number
  formattedCouponDiscount: string
  shippingAmount: string
  formattedRetailDeliveryFee?: string
  shippingAmountNumeric: number
  shippingEmailAddress: string
  shippingMethod: string
  shippingPhoneNumber: string
  taxAmount: string
  countryTaxAmount?: string
  regionTaxAmount?: string
  taxAmountNumeric: number
  shippingAddress: Address
  couponTotal: string
  giftRecipientEmail?: string
  giftRecipientMessage?: string
  isGift?: boolean
  isCatalogMailing?: boolean
  isDemoOrder?: boolean
  isCustomerOptedInForLoyalty?: boolean
}

export interface PaypalInfo {
  clientId: string
}
export enum PayPalImages {
  PAYPAL = 'https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png',
  PAYLATER = 'https://www.paypalobjects.com/webstatic/en_US/i/buttons/ppcredit-logo-small.png',
  IDEAL = 'https://www.paypalobjects.com/images/checkout/alternative_payments/paypal_ideal_color.svg',
  BANCONTACT = 'https://www.paypalobjects.com/images/checkout/alternative_payments/paypal_bancontact_color.svg'
}

export enum PaymentSection {
  Credit = 'credit',
  Debit = 'debit',
  PAYPAL = 'paypal',
  BANCONTACT = 'bancontact',
  IDEAL = 'ideal',
}

export enum PayPalFunding {
  PAYPAL = 'paypal',
  BANCONTACT = 'bancontact',
  IDEAL = 'ideal',
  CREDIT = 'credit',
  PAYLATER = 'paylater',
  SEPA = 'sepa',
  CARD = 'card'
}

// Which PayPal integration the add-payment step offers for saving a payment method:
//   Direct  — legacy PayPal Business, authorized per order (one-time orders, prepaid subs)
//   Recurly — PayPal Complete billing agreement via recurly.PayPal() (monthly subs, behind
//             recurlyPaypalSubscriptions), which Recurly then charges at renewal
// Checkout is not gated on this — it always authorizes against PayPal Business.
export enum PayPalIntegration {
  None = 'none',
  Direct = 'direct',
  Recurly = 'recurly'
}

/**
 * Base global the PayPal SDK is loaded under. The active namespace is
 * `paypal_<country>` (see usePaypal's sdkNamespaceFor) so a cross-border order
 * can load its destination country's SDK without colliding with the
 * preloaded locale SDK already on the page.
 */
export const DEFAULT_PAYPAL_SDK_NAMESPACE = 'paypal'

export enum CheckoutStep {
  ADDRESS = 'addressStep',
  PAYMENT = 'paymentStep',
  GIFT_MESSAGE = 'giftMessageStep',
  SHIPPING ='shippingStep',
  DEMO = 'demoStep',
  AGREEMENT = 'agreement',
  COMPLETE = 'complete'
}

export enum JoinCheckoutStep {
  PERSONAL_INFO = 'personalInfo',
  MAILING_ADDRESS = 'addressStep',
  PAYMENT = 'paymentStep',
  SHIPPING_METHOD = 'shippingMethod',
  AGREEMENT = 'agreement',
  VAT = 'vat',
  COMPLETE = 'complete'
}

export enum MailingCheckoutStep {
  RECIPIENTS = 'recipientsStep',
  PAYMENT = 'paymentStep',
  COMPLETE = 'complete'
}

export interface RecurlyCardDetails {
  cardType: string
  cardLast4: string
  cardFirst6: string
  cardFirstName: string
  cardLastName: string
  cardExpMonth: number
  cardExpYear: number
  cardFundingSource: string
}

// recurly.token()'s runtime response carries a `card` object that the
// DefinitelyTyped `TokenPayload` ({ id, type }) does not declare — this is the
// repo-owned shape the Signifyd card mapping reads after casting.
export interface RecurlyTokenResult {
  id: string
  card?: {
    brand: string
    first_six: string
    last_four: string
    exp_month: number
    exp_year: number
    funding_source: string
  }
}

export interface UpdateRecurlyPaymentMethodPayload {
  recurlyToken?: string | undefined
  billingInfoId?: string | undefined
  saveToProfile?: boolean
  isPaypal?: boolean
  catalogMailingId?: string
  isSubscription?: boolean
  subscriptionId?: string
  cardDetails?: RecurlyCardDetails
}

export interface UpdatePaymentMethodPayload {
  payment?: PaymentInformation | null
  authorizationId?: string | null
  catalogMailingId?: string
  isSubscription?: boolean
}
export type CheckoutStepsType = JoinCheckoutStep | MailingCheckoutStep | CheckoutStep
